import styled, { css } from 'styled-components';
import Image from 'next/image';
import * as TypographyStyles from '../Typography/Typography.styles';
import { Button } from '@/components/UI/Button/Button';

export const Count = styled(TypographyStyles.Typography.Caption)`
  position: absolute;
  visibility: hidden;
  opacity: 0;
  right: 50%;
  bottom: 30px;
  transform: translateX(50%);
  z-index: 1;
  border-radius: 14px;
  background-color: ${({ theme }) => theme.palette.overlay['40']};
  padding: 5px 10px;
  letter-spacing: -1.2px;
  color: ${({ theme }) => theme.palette.system.white};
  transition: opacity 0.2s linear;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    visibility: visible;
    opacity: 1;
    bottom: 10px;
    right: 15px;
    transform: none;
    border-radius: 7px;
    padding: 5px 12px;
  }
`;

export const ControlButton = styled(Button).attrs({
  appearance: 'transparent',
  hasPadding: false,
})`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  border-radius: 50px;
  background: ${({ theme }) => theme.palette.overlay['60']};
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s linear;
`;

export const ArrowButton = styled(ControlButton)<{
  $direction: 'left' | 'right';
}>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 10px;
  background-image: url('/images/arrow.svg');
  background-position: center;
  background-repeat: no-repeat;
  background-size: 9px 16px;
  ${({ $direction }) =>
    $direction === 'left' &&
    css`
      right: unset;
      left: 10px;
      transform: translateY(-50%) rotate(180deg);
    `};
`;

export const ZoomButton = styled(ControlButton)`
  position: absolute;
  top: 10px;
  right: 10px;
  background-image: url('/images/zoom.svg');
  background-position: center;
  background-repeat: no-repeat;
`;

export const SliderWrapper = styled.div<{ $loading?: boolean }>`
  position: relative;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100vw;
  }

  &:hover {
    ${Count},
    ${ControlButton} {
      opacity: 1;
      visibility: visible;
    }
  }
`;

export const Slider = styled.div<{
  $photosCount?: number;
}>`
  display: grid;
  height: 480px;
  overflow-x: scroll;
  cursor: pointer;
  overflow-y: hidden;
  user-select: none;
  scrollbar-width: none;
  scroll-snap-type: x mandatory;
  -webkit-tap-highlight-color: transparent;
  border-radius: 14px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 330px;
    border-radius: 0;
  }

  &::-webkit-scrollbar {
    display: none;
  }

  grid-template-columns: repeat(${({ $photosCount }) => $photosCount}, 100%);
`;

export const Slide = styled.div`
  width: 100%;
  height: 330px;
  display: flex;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
`;

export const SlideWrapper = styled.div<{
  $photo: string;
}>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background-image: url(${({ $photo }) => $photo});
  background-size: cover;
  background-position: center;
  z-index: 0;
  pointer-events: none;
  will-change: transform;
  contain: paint;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    backdrop-filter: blur(20px);
    background-color: ${({ theme }) => theme.palette.overlay['40']};
  }
`;

export const SlideImage = styled(Image)`
  width: 100%;
  height: 100%;
  object-fit: contain;
  margin: auto;
  position: relative;
  z-index: 2;
  user-drag: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    object-fit: cover;
  }
`;

export const Thumbs = styled.div`
  display: flex;
  gap: 2px;
  margin-top: 5px;
`;

export const Thumb = styled.div`
  position: relative;
  border-radius: 7px;
  overflow: hidden;
  width: 70px;
  height: 50px;
  cursor: pointer;
  background-color: ${({ theme }) => theme.palette.system.white};
`;

export const ThumbImage = styled.img<{ $isActive: boolean }>`
  max-width: 100%;
  width: 100%;
  height: 100%;
  object-fit: cover;
  user-drag: none;
  opacity: ${({ $isActive }) => ($isActive ? 1 : 0.75)};
  overflow-clip-margin: unset;
`;

export const ThumbLabel = styled.div`
  display: flex;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  position: absolute;
  align-items: center;
  font-size: 12px;
  justify-content: center;
  color: ${({ theme }) => theme.palette.system.white};
  background-color: ${({ theme }) => theme.palette.overlay['50']};
`;
